import React, { Component } from "react";
import PropTypes from "prop-types";
import { extent as d3Extent, min, max } from "d3-array";

import {
  head,
  last,
  isDefined,
  isNotDefined,
  clearCanvas,
  shallowEqual,
  identity,
  noop,
  functor,
  getLogger,
} from "./utils";

/* eslint-disable no-unused-vars */
import {
  mouseBasedZoomAnchor,
  lastVisibleItemBasedZoomAnchor,
  rightDomainBasedZoomAnchor,
} from "./utils/zoomBehavior";
/* eslint-enable no-unused-vars */

import {
  getNewChartConfig,
  getChartConfigWithUpdatedYScales,
  getCurrentCharts,
  getCurrentItem,
} from "./utils/ChartDataUtil";

import EventCapture from "./EventCapture";

import CanvasContainer from "./CanvasContainer";
import evaluator from "./scale/evaluator";
import { recordChartSizingEvent } from "../../components/ChartWrapper/utils/chartSizingDebug";
import { isLastBarVisibleInExtents } from "../../components/ChartWrapper/utils/chartHorizontalState.js";
import {
  isRafBlocked,
  shouldRafRedraw,
  shouldAbortZoomIn,
} from "./rafDrawDecisions.js";
import {
  latestChartConfigOf,
  shouldSkipStateUpdateDuringGesture,
  POST_ZOOM_QUIET_MS,
} from "./chartCanvasDecisions.js";

const log = getLogger("ChartCanvas");
const DEBUG_CHART_SIZING = import.meta.env.DEV;

const CANDIDATES_FOR_RESET = [
  "seriesName",
  /* "data",*/
  /* "xAccessor",*/
];

function shouldResetChart(thisProps, nextProps) {
  return !CANDIDATES_FOR_RESET.every((key) => {
    const result = shallowEqual(thisProps[key], nextProps[key]);
    return result;
  });
}

function getCursorStyle() {
  const tooltipStyle = `
	.react-stockcharts-grabbing-cursor {
		pointer-events: all;
		cursor: -moz-grabbing;
		cursor: -webkit-grabbing;
		cursor: grabbing;
	}
	.react-stockcharts-crosshair-cursor {
		pointer-events: all;
		cursor: crosshair;
	}
	.react-stockcharts-tooltip-hover {
		pointer-events: all;
		cursor: pointer;
	}
	.react-stockcharts-avoid-interaction {
		pointer-events: none;
	}
	.react-stockcharts-enable-interaction {
		pointer-events: all;
	}
	.react-stockcharts-tooltip {
		pointer-events: all;
		cursor: pointer;
	}
	.react-stockcharts-default-cursor {
		cursor: default;
	}
	.react-stockcharts-move-cursor {
		cursor: move;
	}
	.react-stockcharts-pointer-cursor {
		cursor: pointer;
	}
	.react-stockcharts-ns-resize-cursor {
		cursor: ns-resize;
	}
	.react-stockcharts-ew-resize-cursor {
		cursor: ew-resize;
	}
	.react-stockcharts-text-cursor {
		cursor: text;
	}`;
  return <style type="text/css">{tooltipStyle}</style>;
}

function getDimensions(props) {
  return {
    height: props.height - props.margin.top - props.margin.bottom,
    width: props.width - props.margin.left - props.margin.right,
  };
}

function getXScaleDirection(flipXScale) {
  return flipXScale ? -1 : 1;
}

function calculateFullData(props) {
  const {
    data: fullData,
    plotFull,
    xScale,
    clamp,
    pointsPerPxThreshold,
    flipXScale,
  } = props;
  const { xAccessor, displayXAccessor, minPointsPerPxThreshold } = props;

  const useWholeData = isDefined(plotFull) ? plotFull : xAccessor === identity;

  const { filterData } = evaluator({
    xScale,
    useWholeData,
    clamp,
    pointsPerPxThreshold,
    minPointsPerPxThreshold,
    flipXScale,
  });

  return {
    xAccessor,
    displayXAccessor: displayXAccessor || xAccessor,
    xScale: xScale.copy(),
    fullData,
    filterData,
  };
}
function resetChart(props, firstCalculation = false) {
  if (process.env.NODE_ENV !== "production") {
    if (!firstCalculation) log("CHART RESET");
  }

  const state = calculateState(props);
  const { xAccessor, displayXAccessor, fullData } = state;
  const { plotData: initialPlotData, xScale } = state;
  const { postCalculator, children } = props;

  const plotData = postCalculator(initialPlotData);

  const dimensions = getDimensions(props);
  const chartConfig = getChartConfigWithUpdatedYScales(
    getNewChartConfig(dimensions, children),
    { plotData, xAccessor, displayXAccessor, fullData },
    xScale.domain(),
  );

  return {
    ...state,
    xScale,
    plotData,
    chartConfig,
  };
}

function updateChart(
  newState,
  initialXScale,
  props,
  lastItemWasVisible,
  initialChartConfig,
) {
  const { fullData, xScale, xAccessor, displayXAccessor, filterData } =
    newState;

  const lastItem = last(fullData);
  const [start, end] = initialXScale.domain();

  if (process.env.NODE_ENV !== "production") {
    log("TRIVIAL CHANGE");
  }

  const { postCalculator, children, padding, flipXScale } = props;
  const horizontalResizePolicy =
    props.layoutSnapshot?.horizontalResizePolicy || "preserve_x_extents";
  const direction = getXScaleDirection(flipXScale);
  const dimensions = getDimensions(props);

  const updatedXScale = setXRange(xScale, dimensions, padding, direction);

  let initialPlotData;
  if (!lastItemWasVisible || end >= xAccessor(lastItem)) {
    // resize comes here...
    // get plotData between [start, end] and do not change the domain
    const [rangeStart, rangeEnd] = initialXScale.range();
    const [newRangeStart, newRangeEnd] = updatedXScale.range();
    const newStart = start;

    const lastItemX = initialXScale(xAccessor(lastItem));
    const response = filterData(
      fullData,
      [newStart, end],
      xAccessor,
      updatedXScale,
      { fallbackStart: start, fallbackEnd: { lastItem, lastItemX } },
    );
    initialPlotData = response.plotData;
    updatedXScale.domain(response.domain);
  } else if (lastItemWasVisible && end < xAccessor(lastItem)) {
    // this is when a new item is added and last item was visible
    // so slide over and show the new item also

    // get plotData between [xAccessor(l) - (end - start), xAccessor(l)] and DO change the domain
    const dx = initialXScale(xAccessor(lastItem)) - initialXScale.range()[1];
    const [newStart, newEnd] = initialXScale
      .range()
      .map((x) => x + dx)
      .map(initialXScale.invert);

    const response = filterData(
      fullData,
      [newStart, newEnd],
      xAccessor,
      updatedXScale,
    );
    initialPlotData = response.plotData;
    updatedXScale.domain(response.domain); // if last item was visible, then shift
  }
  // plotData = getDataOfLength(fullData, showingInterval, plotData.length)
  let plotData = postCalculator(initialPlotData);

  // Garantir que último candle permanece visível se estava visível
  if (lastItemWasVisible) {
    const lastItemX = xAccessor(lastItem);
    const [domainStart, domainEnd] = updatedXScale.domain();

    // Se último candle ficou fora do domain visível, ajustar
    if (lastItemX > domainEnd || lastItemX < domainStart) {
      const domainWidth = domainEnd - domainStart;
      const buffer = 0.5;
      const newEnd = lastItemX + buffer;
      const newStart = newEnd - domainWidth;

      const response = filterData(
        fullData,
        [newStart, newEnd],
        xAccessor,
        updatedXScale,
      );
      plotData = postCalculator(response.plotData);
      updatedXScale.domain(response.domain);
    }
  }

  const chartConfig = getChartConfigWithUpdatedYScales(
    getNewChartConfig(dimensions, children, initialChartConfig),
    { plotData, xAccessor, displayXAccessor, fullData },
    updatedXScale.domain(),
  );

  return {
    xScale: updatedXScale,
    xAccessor,
    chartConfig,
    plotData,
    fullData,
    filterData,
  };
}

function calculateState(props) {
  const {
    xAccessor: inputXAccesor,
    xExtents: xExtentsProp,
    data,
    padding,
    flipXScale,
  } = props;

  if (process.env.NODE_ENV !== "production" && isDefined(props.xScale.invert)) {
    for (let i = 1; i < data.length; i++) {
      const prev = data[i - 1];
      const curr = data[i];
      if (inputXAccesor(prev) > inputXAccesor(curr)) {
        throw new Error(
          "'data' is not sorted on 'xAccessor', send 'data' sorted in ascending order of 'xAccessor'",
        );
      }
    }
  }

  const direction = getXScaleDirection(flipXScale);
  const dimensions = getDimensions(props);

  const extent =
    typeof xExtentsProp === "function"
      ? xExtentsProp(data)
      : d3Extent(
          xExtentsProp
            .map((d) => functor(d))
            .map((each) => each(data, inputXAccesor)),
        );

  const { xAccessor, displayXAccessor, xScale, fullData, filterData } =
    calculateFullData(props);
  const updatedXScale = setXRange(xScale, dimensions, padding, direction);

  let { plotData, domain } = filterData(
    fullData,
    extent,
    inputXAccesor,
    updatedXScale,
  );

  // Self-heal: a stale over-zoomed extent (e.g. persisted from a previous session or
  // recovered after an ErrorBoundary reset) can produce fewer visible bars than the
  // chart is willing to render. Rather than throw and surface the dev error, widen the
  // view to the tail of fullData when we have enough data to do so. If fullData itself
  // is tiny, we still throw in dev — that's a real setup bug, not recoverable state.
  const MIN_PLOT_POINTS = 5;
  if (plotData.length <= 1 && fullData.length >= MIN_PLOT_POINTS) {
    const tail = fullData.slice(-MIN_PLOT_POINTS);
    const safeExtent = [
      inputXAccesor(tail[0]),
      inputXAccesor(tail[tail.length - 1]),
    ];
    const recovered = filterData(
      fullData,
      safeExtent,
      inputXAccesor,
      updatedXScale,
    );
    plotData = recovered.plotData;
    domain = recovered.domain;
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        `[ChartCanvas] xExtents produced ${plotData.length}; widened to last ${MIN_PLOT_POINTS} bars`,
      );
    }
  }

  if (process.env.NODE_ENV !== "production" && plotData.length <= 1) {
    throw new Error(
      `Showing ${plotData.length} datapoints, review the 'xExtents' prop of ChartCanvas`,
    );
  }
  return {
    plotData,
    xScale: updatedXScale.domain(domain),
    xAccessor,
    displayXAccessor,
    fullData,
    filterData,
  };
}

function setXRange(xScale, dimensions, padding, direction = 1) {
  if (xScale.rangeRoundPoints) {
    if (isNaN(padding))
      throw new Error("padding has to be a number for ordinal scale");
    xScale.rangeRoundPoints([0, dimensions.width], padding);
  } else if (xScale.padding) {
    if (isNaN(padding))
      throw new Error("padding has to be a number for ordinal scale");
    xScale.range([0, dimensions.width]);
    xScale.padding(padding / 2);
  } else {
    const { left, right } = isNaN(padding)
      ? padding
      : { left: padding, right: padding };
    if (direction > 0) {
      xScale.range([left, dimensions.width - right]);
    } else {
      xScale.range([dimensions.width - right, left]);
    }
  }
  return xScale;
}

function pinchCoordinates(pinch) {
  const { touch1Pos, touch2Pos } = pinch;

  return {
    topLeft: [
      Math.min(touch1Pos[0], touch2Pos[0]),
      Math.min(touch1Pos[1], touch2Pos[1]),
    ],
    bottomRight: [
      Math.max(touch1Pos[0], touch2Pos[0]),
      Math.max(touch1Pos[1], touch2Pos[1]),
    ],
  };
}

class ChartCanvas extends Component {
  constructor(props) {
    super(props);
    this.getDataInfo = this.getDataInfo.bind(this);
    this.getCanvasContexts = this.getCanvasContexts.bind(this);

    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleZoom = this.handleZoom.bind(this);
    this.handleWindowBlur = this.handleWindowBlur.bind(this);
    this.handlePinchZoom = this.handlePinchZoom.bind(this);
    this.handlePinchZoomEnd = this.handlePinchZoomEnd.bind(this);
    this.handlePan = this.handlePan.bind(this);
    this.handlePanEnd = this.handlePanEnd.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);
    this.handleContextMenu = this.handleContextMenu.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);

    this.panHelper = this.panHelper.bind(this);
    this.pinchZoomHelper = this.pinchZoomHelper.bind(this);
    this.xAxisZoom = this.xAxisZoom.bind(this);
    this.yAxisZoom = this.yAxisZoom.bind(this);
    this.yAxisZoomStart = this.yAxisZoomStart.bind(this);
    this.yAxisZoomUpdate = this.yAxisZoomUpdate.bind(this);
    this.yAxisZoomCommit = this.yAxisZoomCommit.bind(this);
    this.resetYDomain = this.resetYDomain.bind(this);
    this.calculateStateForDomain = this.calculateStateForDomain.bind(this);
    this.generateSubscriptionId = this.generateSubscriptionId.bind(this);
    this.draw = this.draw.bind(this);
    this.redraw = this.redraw.bind(this);
    this.getAllPanConditions = this.getAllPanConditions.bind(this);
    this.handleBaselineRecenter = this.handleBaselineRecenter.bind(this);
    this.redrawAfterDataUpdate = this.redrawAfterDataUpdate.bind(this);

    this.subscriptions = [];
    this.subscribe = this.subscribe.bind(this);
    this.unsubscribe = this.unsubscribe.bind(this);
    this.amIOnTop = this.amIOnTop.bind(this);
    this.saveEventCaptureNode = this.saveEventCaptureNode.bind(this);
    this.saveCanvasContainerNode = this.saveCanvasContainerNode.bind(this);
    this.setCursorClass = this.setCursorClass.bind(this);
    this.getMutableState = this.getMutableState.bind(this);
    // this.canvasDrawCallbackList = [];
    this.interactiveState = [];
    this.panInProgress = false;

    this.state = {};
    this.mutableState = {};
    this.lastSubscriptionId = 0;

    // FIX #2: Temporary Y-axis domain tracking (like chart separator resize)
    this.tempYDomains = null; // { [chartId]: domain } during active drag
    this.yAxisDragInProgress = false;
    this.waitingForYAxisAnimationFrame = false; // RAF throttling flag (same pattern as pan)

    // Throttle mousemove draws to max 60 FPS
    this.lastMouseMoveDrawTime = 0;
    this.mouseMoveThrottleInterval = 17; // ~58 FPS (slightly under 60 to account for timing variations)

    // Zoom queue system - RAF throttle pattern (same as yAxisZoom)
    this.pendingZoom = null;
    this.zoomInProgress = false;
    this.lastZoomTime = 0;
    this._quietWindowReapplyTimer = null; // #357 deferred re-apply timer
    this.waitingForZoomAnimationFrame = false; // RAF throttle flag
    this.accumulatedZoomSteps = 0; // Accumulate rapid scroll steps
    this.pendingZoomMouseXY = null;
    this.pendingZoomEvent = null;
    this.lastZoomedExtents = null; // Track extents we expect back as prop (prevents bounce)

    // Resize tracking (synchronous path — no RAF deferral needed)

    // React update tracking - prevent RAF from interfering during React updates
    this.reactUpdateInProgress = false;

    // Deferred drawing - children skip componentDidUpdate drawing, we draw in setState callback
    this.deferDrawing = false;

    // Deferred clear - first component to draw will clear the canvas
    this.needsCanvasClear = false;
    this.clearIfNeeded = this.clearIfNeeded.bind(this);
  }
  saveEventCaptureNode(node) {
    this.eventCaptureNode = node;
  }
  saveCanvasContainerNode(node) {
    this.canvasContainerNode = node;
  }
  getMutableState() {
    return this.mutableState;
  }
  getDataInfo() {
    return {
      ...this.state,
      fullData: this.fullData,
    };
  }
  getCanvasContexts() {
    if (this.canvasContainerNode) {
      return this.canvasContainerNode.getCanvasContexts();
    }
  }
  generateSubscriptionId() {
    this.lastSubscriptionId++;
    return this.lastSubscriptionId;
  }
  clearBothCanvas() {
    const canvases = this.getCanvasContexts();
    if (canvases && canvases.axes) {
      clearCanvas(
        [
          canvases.axes,
          // canvases.hover,
          canvases.mouseCoord,
        ],
        this.props.ratio,
      );
    }
  }
  clearMouseCanvas() {
    const canvases = this.getCanvasContexts();
    if (canvases && canvases.mouseCoord) {
      clearCanvas(
        [
          canvases.mouseCoord,
          // canvases.hover,
        ],
        this.props.ratio,
      );
    }
  }
  clearThreeCanvas() {
    const canvases = this.getCanvasContexts();
    if (canvases && canvases.axes) {
      clearCanvas(
        [
          canvases.axes,
          // canvases.hover,
          canvases.mouseCoord,
          canvases.bg,
        ],
        this.props.ratio,
      );
    }
  }
  clearIfNeeded() {
    // Called by first component to draw - clears canvas once, then resets flag
    if (this.needsCanvasClear) {
      this.needsCanvasClear = false;
      this.clearThreeCanvas();
    }
  }
  // Refresh interactive components after the data prop changes (new bars, symbol
  // switch reload, etc.) and redraw the canvas atomically.
  // - triggerEvent intentionally OMITS chartConfig: the listener path stores it
  //   verbatim and single-config consumers expect one config, not the array.
  // - draw() IS array-aware (GenericComponent.draw finds this chart's config), so
  //   it MUST receive chartConfig — otherwise canvas interactive components
  //   (order/OTO/OCO/position lines) keep the stale cached yScale and render at
  //   the wrong Y until a pan/hover/zoom forces a refresh.
  // Issue #351: the symbol-switch reload arrives via this path; omitting
  // chartConfig left order lines anchored to the previous yScale. Mirrors the
  // resize path and the yAxis* paths, which already pass chartConfig to draw().
  redrawAfterDataUpdate(state) {
    this.triggerEvent("data-update", {
      xScale: state.xScale,
      plotData: state.plotData,
    });
    this.clearThreeCanvas();
    this.draw({
      trigger: "data-update",
      force: true,
      chartConfig: state.chartConfig,
    });
  }
  subscribe(id, rest) {
    const {
      getPanConditions = functor({
        draggable: false,
        panEnabled: true,
      }),
    } = rest;
    this.subscriptions = this.subscriptions.concat({
      id,
      ...rest,
      getPanConditions,
    });
  }
  unsubscribe(id) {
    this.subscriptions = this.subscriptions.filter((each) => each.id !== id);
  }
  getAllPanConditions() {
    return this.subscriptions.map((each) => each.getPanConditions());
  }
  setCursorClass(className) {
    if (this.eventCaptureNode != null) {
      this.eventCaptureNode.setCursorClass(className);
    }
  }
  amIOnTop(id) {
    const dragableComponents = this.subscriptions.filter(
      (each) => each.getPanConditions().draggable,
    );

    return dragableComponents.length > 0 && last(dragableComponents).id === id;
  }
  handleContextMenu(mouseXY, e) {
    const { xAccessor, chartConfig, plotData, xScale } = this.state;

    const currentCharts = getCurrentCharts(chartConfig, mouseXY);
    const currentItem = getCurrentItem(xScale, xAccessor, mouseXY, plotData);

    this.triggerEvent(
      "contextmenu",
      {
        mouseXY,
        currentItem,
        currentCharts,
      },
      e,
    );
  }
  calculateStateForDomain(newDomain) {
    const {
      xAccessor,
      displayXAccessor,
      xScale: initialXScale,
      chartConfig: initialChartConfig,
      plotData: initialPlotData,
    } = this.state;
    const { filterData } = this.state;
    const { fullData } = this;
    const { postCalculator, onVisibleBarsChange } = this.props;

    const { plotData: beforePlotData, domain } = filterData(
      fullData,
      newDomain,
      xAccessor,
      initialXScale,
      {
        currentPlotData: initialPlotData,
        currentDomain: initialXScale.domain(),
      },
    );

    const plotData = postCalculator(beforePlotData);
    const updatedScale = initialXScale.copy().domain(domain);
    const chartConfig = getChartConfigWithUpdatedYScales(
      initialChartConfig,
      { plotData, xAccessor, displayXAccessor, fullData },
      updatedScale.domain(),
    );

    // Notify about visible bars change if callback is provided
    // Use setTimeout to avoid interfering with the zoom mechanics
    if (onVisibleBarsChange && plotData && plotData.length > 0) {
      const visibleBars = plotData.length;

      // Call the callback asynchronously to avoid blocking zoom
      setTimeout(() => {
        try {
          onVisibleBarsChange(visibleBars);
        } catch (error) {
          console.error("Error in onVisibleBarsChange callback:", error);
        }
      }, 0);
    }

    return {
      xScale: updatedScale,
      plotData,
      chartConfig,
    };
  }
  pinchZoomHelper(initialPinch, finalPinch) {
    const { xScale: initialPinchXScale } = initialPinch;

    const {
      xScale: initialXScale,
      chartConfig: initialChartConfig,
      plotData: initialPlotData,
      xAccessor,
      displayXAccessor,
    } = this.state;
    const { filterData } = this.state;
    const { fullData } = this;
    const { postCalculator } = this.props;

    const { topLeft: iTL, bottomRight: iBR } = pinchCoordinates(initialPinch);
    const { topLeft: fTL, bottomRight: fBR } = pinchCoordinates(finalPinch);

    const e = initialPinchXScale.range()[1];

    const xDash = Math.round(
      -(iBR[0] * fTL[0] - iTL[0] * fBR[0]) / (iTL[0] - iBR[0]),
    );
    const yDash = Math.round(
      e +
        ((e - iBR[0]) * (e - fTL[0]) - (e - iTL[0]) * (e - fBR[0])) /
          (e - iTL[0] - (e - iBR[0])),
    );

    const x = Math.round((-xDash * iTL[0]) / (-xDash + fTL[0]));
    const y = Math.round(
      e - ((yDash - e) * (e - iTL[0])) / (yDash + (e - fTL[0])),
    );

    const newDomain = [x, y].map(initialPinchXScale.invert);
    // var domainR = initial.right + right;

    const { plotData: beforePlotData, domain } = filterData(
      fullData,
      newDomain,
      xAccessor,
      initialPinchXScale,
      {
        currentPlotData: initialPlotData,
        currentDomain: initialXScale.domain(),
      },
    );

    const plotData = postCalculator(beforePlotData);
    const updatedScale = initialXScale.copy().domain(domain);

    const mouseXY = finalPinch.touch1Pos;
    const chartConfig = getChartConfigWithUpdatedYScales(
      initialChartConfig,
      { plotData, xAccessor, displayXAccessor, fullData },
      updatedScale.domain(),
    );
    const currentItem = getCurrentItem(
      updatedScale,
      xAccessor,
      mouseXY,
      plotData,
    );

    return {
      chartConfig,
      xScale: updatedScale,
      plotData,
      mouseXY,
      currentItem,
    };
  }
  cancelDrag() {
    this.eventCaptureNode.cancelDrag();
    this.triggerEvent("dragcancel");
  }
  handlePinchZoom(initialPinch, finalPinch, e) {
    if (!this.waitingForPinchZoomAnimationFrame) {
      this.waitingForPinchZoomAnimationFrame = true;
      const state = this.pinchZoomHelper(initialPinch, finalPinch);

      this.triggerEvent("pinchzoom", state, e);

      this.finalPinch = finalPinch;

      requestAnimationFrame(() => {
        this.clearBothCanvas();
        this.draw({ trigger: "pinchzoom" });
        this.waitingForPinchZoomAnimationFrame = false;
      });
    }
  }
  handlePinchZoomEnd(initialPinch, e) {
    const { xAccessor } = this.state;

    if (this.finalPinch) {
      const state = this.pinchZoomHelper(initialPinch, this.finalPinch);
      const { xScale } = state;
      this.triggerEvent("pinchzoom", state, e);

      this.finalPinch = null;

      this.clearThreeCanvas();

      const { fullData } = this;
      const firstItem = head(fullData);

      const start = head(xScale.domain());
      const end = xAccessor(firstItem);
      const { onLoadMore, onXExtentsChange } = this.props;

      this.setState(state, () => {
        this.draw({ trigger: "pinchzoom" });

        if (start < end) {
          onLoadMore(start, end);
        }

        // Notify about xExtents change after pinch zoom completes
        if (onXExtentsChange) {
          const xExtents = xScale.domain();
          setTimeout(() => {
            try {
              onXExtentsChange(xExtents);
            } catch (error) {
              console.error("Error in onXExtentsChange callback:", error);
            }
          }, 0);
        }
      });
    }
  }
  handleZoom(zoomDirection, mouseXY, e) {
    if (this.panInProgress) return;

    // Accumulate zoom steps for rapid scrolling (batches multiple wheel events)
    this.accumulatedZoomSteps += zoomDirection;
    this.pendingZoomMouseXY = mouseXY;
    this.pendingZoomEvent = e;

    // RAF throttle - only process once per frame (same pattern as yAxisZoom)
    if (this.waitingForZoomAnimationFrame) return;

    this.waitingForZoomAnimationFrame = true;

    requestAnimationFrame(() => {
      // DON'T reset waitingForZoomAnimationFrame here - wait for setState callback
      // This prevents a new RAF from scheduling and reading stale state

      // Get accumulated steps and reset
      const totalZoomSteps = this.accumulatedZoomSteps;
      const currentMouseXY = this.pendingZoomMouseXY;
      const currentEvent = this.pendingZoomEvent;
      this.accumulatedZoomSteps = 0;
      this.pendingZoomMouseXY = null;
      this.pendingZoomEvent = null;

      if (totalZoomSteps === 0) {
        this.waitingForZoomAnimationFrame = false; // Reset on early return
        return;
      }

      this.zoomInProgress = true;

      // --- Expensive calculation now inside RAF ---
      const { xAccessor, xScale: initialXScale } = this.state;
      const { zoomMultiplier } = this.props;
      const { fullData } = this;

      // Anchor zoom at the right edge of the chart (last visible bar stays in place)
      const range = initialXScale.range();
      const cx = range[1]; // Right edge of chart

      // Apply accumulated zoom steps (combines rapid scrolling into one operation)
      const c =
        totalZoomSteps > 0
          ? Math.pow(zoomMultiplier, Math.abs(totalZoomSteps))
          : Math.pow(1 / zoomMultiplier, Math.abs(totalZoomSteps));
      const newDomain = range
        .map((x) => cx + (x - cx) * c)
        .map(initialXScale.invert);

      const { xScale, plotData, chartConfig } =
        this.calculateStateForDomain(newDomain);

      // Min-zoom clamp. A fast wheel spin accumulates enough steps to shrink the
      // visible domain below ChartCanvas's ≤1-bar dev guard, which throws into the
      // ErrorBoundary. Abort the zoom-in when the resulting plot would drop below
      // MIN_PLOT_POINTS — but only when fullData actually has enough bars to
      // satisfy the threshold, so legitimately sparse charts still zoom.
      if (shouldAbortZoomIn(totalZoomSteps, plotData.length, fullData.length)) {
        this.zoomInProgress = false;
        this.waitingForZoomAnimationFrame = false;
        return;
      }

      const currentItem = getCurrentItem(
        xScale,
        xAccessor,
        currentMouseXY,
        plotData,
      );
      const currentCharts = getCurrentCharts(chartConfig, currentMouseXY);

      const firstItem = head(fullData);
      const start = head(xScale.domain());
      const end = xAccessor(firstItem);
      const { onLoadMore, onXExtentsChange } = this.props;

      this.mutableState = {
        mouseXY: currentMouseXY,
        currentItem,
        currentCharts,
      };

      this.triggerEvent(
        "zoom",
        {
          xScale,
          plotData,
          chartConfig,
          mouseXY: currentMouseXY,
          currentCharts,
          currentItem,
          show: true,
        },
        currentEvent,
      );

      // Save zoom time to skip data updates briefly after zoom
      this.lastZoomTime = Date.now();

      this.setState(
        {
          xScale,
          plotData,
          chartConfig,
        },
        () => {
          // Clear and draw in same callback to prevent flicker
          this.clearThreeCanvas();
          this.draw({ trigger: "zoom", force: true });

          this.zoomInProgress = false;
          this.waitingForZoomAnimationFrame = false; // Reset AFTER state committed

          // Track expected extents - prevents bounce when prop comes back
          const zoomedExtents = xScale.domain();
          this.lastZoomedExtents = [zoomedExtents[0], zoomedExtents[1]]; // Clone array

          if (start < end) {
            onLoadMore(start, end);
          }

          // Notify about xExtents change after zoom completes
          if (onXExtentsChange) {
            setTimeout(() => {
              try {
                onXExtentsChange(this.lastZoomedExtents);
              } catch (error) {
                console.error("Error in onXExtentsChange callback:", error);
              }
            }, 0);
          }
        },
      );
    });
  }
  xAxisZoom(newDomain) {
    const { xScale, plotData, chartConfig } =
      this.calculateStateForDomain(newDomain);
    this.clearThreeCanvas();

    const { xAccessor } = this.state;
    const { fullData } = this;
    const firstItem = head(fullData);
    const start = head(xScale.domain());
    const end = xAccessor(firstItem);
    const { onLoadMore, onXExtentsChange } = this.props;

    this.setState(
      {
        xScale,
        plotData,
        chartConfig,
      },
      () => {
        // Trigger event to update moreProps for interactive components
        // Note: Don't pass chartConfig here - components get it from context
        // Passing the array would break getMorePropsForChart which expects a single config
        this.triggerEvent("pan", { xScale, plotData });
        // Pass all updated values so components get fresh scales
        this.draw({
          trigger: "xAxisZoom",
          force: true,
          xScale,
          plotData,
          chartConfig,
        });

        if (start < end) onLoadMore(start, end);

        // Notify about xExtents change after axis zoom completes
        if (onXExtentsChange) {
          const xExtents = xScale.domain();
          setTimeout(() => {
            try {
              onXExtentsChange(xExtents);
            } catch (error) {
              console.error("Error in onXExtentsChange callback:", error);
            }
          }, 0);
        }
      },
    );
  }
  yAxisZoom(chartId, newDomain) {
    // Reset (double-click) - execute immediately
    if (newDomain === null) {
      const { chartConfig: initialChartConfig } = this.state;
      const chartConfig = initialChartConfig.map((each) => {
        if (each.id === chartId) {
          const { yScale, yExtentsCalculator } = each;
          const { plotData, xAccessor, displayXAccessor } = this.state;
          const xDomain = this.state.xScale.domain();
          const autoYDomain = yExtentsCalculator
            ? yExtentsCalculator({
                plotData,
                xDomain,
                xAccessor,
                displayXAccessor,
                fullData: this.fullData,
              })
            : yScale.domain();
          return {
            ...each,
            yScale: yScale.copy().domain(autoYDomain),
            yPanEnabled: false,
          };
        }
        return each;
      });
      // Use setState callback to ensure clear+draw happen atomically (no flash)
      this.setState({ chartConfig }, () => {
        // Trigger event so listener() updates moreProps on all subscribed components
        this.triggerEvent("pan", { chartConfig });
        this.clearThreeCanvas();
        this.draw({ trigger: "yAxisReset", force: true, chartConfig });
      });
      return;
    }

    // Drag - throttle with RAF to prevent excessive setState calls
    this.pendingYAxisZoom = { chartId, newDomain };

    if (!this.waitingForYAxisAnimationFrame) {
      this.waitingForYAxisAnimationFrame = true;
      requestAnimationFrame(() => {
        this.waitingForYAxisAnimationFrame = false;

        if (!this.pendingYAxisZoom) return;

        const { chartId: cId, newDomain: domain } = this.pendingYAxisZoom;
        this.pendingYAxisZoom = null;

        const { chartConfig: initialChartConfig } = this.state;
        const chartConfig = initialChartConfig.map((each) => {
          if (each.id === cId) {
            return {
              ...each,
              yScale: each.yScale.copy().domain(domain),
              yPanEnabled: true,
            };
          }
          return each;
        });

        // Store the latest chartConfig so newly mounted components can access it
        // during the window between triggerEvent/draw and setState completion
        this.latestChartConfig = chartConfig;

        this.triggerEvent("pan", { chartConfig });
        this.clearBothCanvas();
        this.draw({ trigger: "pan", force: true, chartConfig });

        this.setState({ chartConfig }, () => {
          // Clear latestChartConfig after setState completes
          // Context is now updated, so components will get fresh values from context
          this.latestChartConfig = null;
        });
      });
    }
  }

  // FIX #2: Y-Axis zoom with temporary state (mirrors chart separator pattern)
  yAxisZoomStart() {
    // Initialize temporary domain tracking on drag start
    if (!this.tempYDomains) {
      this.tempYDomains = {};
    }
    this.yAxisDragInProgress = true;
  }

  yAxisZoomUpdate(chartId, newDomain) {
    // Just store the domain - don't redraw during drag
    if (!this.tempYDomains) {
      this.tempYDomains = {};
    }
    this.tempYDomains[chartId] = newDomain;
  }

  yAxisZoomCommit(chartId) {
    // Apply the final domain on drag end (single setState)
    if (!this.tempYDomains || !this.tempYDomains[chartId]) {
      this.yAxisDragInProgress = false;
      return;
    }

    const finalDomain = this.tempYDomains[chartId];

    // Clear temporary state
    this.tempYDomains = null;
    this.yAxisDragInProgress = false;

    // Commit to chartConfig (single setState)
    const { chartConfig: initialChartConfig } = this.state;
    const chartConfig = initialChartConfig.map((each) => {
      if (each.id === chartId) {
        const { yScale } = each;
        return {
          ...each,
          yScale: yScale.copy().domain(finalDomain),
          yPanEnabled: true,
        };
      }
      return each;
    });

    this.setState(
      {
        chartConfig,
      },
      () => {
        // Trigger event so listener() updates moreProps on all subscribed components
        this.triggerEvent("pan", { chartConfig });
        // Force redraw with updated chartConfig so interactive components reposition
        this.clearThreeCanvas();
        this.draw({ trigger: "yAxisZoomCommit", force: true, chartConfig });
      },
    );
  }

  handleBaselineRecenter(event) {
    const { chartId, newDomain } = event.detail;

    // Use setTimeout to avoid any potential conflicts
    setTimeout(() => {
      this.yAxisZoom(chartId, newDomain);
    }, 0);
  }

  componentDidMount() {
    // Listen for baseline recenter events
    window.addEventListener("baselineRecenter", this.handleBaselineRecenter);

    // Issue #337: Windows doesn't fire mouseup when the user alt-tabs mid-pan,
    // so handlePanEnd never runs and panInProgress stays true — silently
    // blocking handleZoom forever. Recover on window blur.
    window.addEventListener("blur", this.handleWindowBlur);

    // Start RAF loop for continuous redrawing (bypasses React render cycle)
    // This ensures the chart redraws on every frame when data changes
    this.rafLoopEnabled = true;
    this.rafLoopId = null;
    this.startRAFLoop();
  }

  componentWillUnmount() {
    // Clean up event listener
    window.removeEventListener("baselineRecenter", this.handleBaselineRecenter);
    window.removeEventListener("blur", this.handleWindowBlur);

    // Stop RAF loop
    this.rafLoopEnabled = false;
    if (this.rafLoopId) {
      cancelAnimationFrame(this.rafLoopId);
      this.rafLoopId = null;
    }

    // #357: cancela um re-apply pendente da janela quieta (evita tocar componente desmontado)
    clearTimeout(this._quietWindowReapplyTimer);
  }

  handleWindowBlur() {
    if (!this.panInProgress) return;
    this.panInProgress = false;
    this.hackyWayToStopPanBeyondBounds__plotData = null;
    this.hackyWayToStopPanBeyondBounds__domain = null;
    this.waitingForPanAnimationFrame = false;
  }

  startRAFLoop() {
    if (!this.rafLoopEnabled) return;

    const rafDraw = () => {
      // Skip during pan, zoom, resize, yAxis drag, or React updates to avoid interference
      if (isRafBlocked(this)) {
        if (this.rafLoopEnabled) {
          this.rafLoopId = requestAnimationFrame(rafDraw);
        }
        return;
      }

      // Only redraw if we have data and canvas is ready
      if (
        this.state.plotData &&
        this.state.plotData.length > 0 &&
        this.canvasContainerNode
      ) {
        // Check if data changed since last draw
        const currentDataLength = this.state.plotData.length;
        const currentLastBar = this.state.plotData[currentDataLength - 1];

        if (
          shouldRafRedraw(
            this.lastDrawnDataLength,
            this.lastDrawnBar,
            currentDataLength,
            currentLastBar,
          )
        ) {
          // Data changed, redraw
          this.clearThreeCanvas();
          this.draw({ force: true, trigger: "raf-loop" });

          this.lastDrawnDataLength = currentDataLength;
          this.lastDrawnBar = currentLastBar;
        }
      }

      // Schedule next frame
      if (this.rafLoopEnabled) {
        this.rafLoopId = requestAnimationFrame(rafDraw);
      }
    };

    // Start the loop
    this.rafLoopId = requestAnimationFrame(rafDraw);
  }

  triggerEvent(type, props, e) {
    this.subscriptions.forEach((each) => {
      const state = {
        ...this.state,
        fullData: this.fullData,
        subscriptions: this.subscriptions,
      };
      each.listener(type, props, state, e);
    });
  }
  draw(props) {
    this.subscriptions.forEach((each) => {
      if (isDefined(each.draw)) each.draw(props);
    });
  }
  redraw() {
    this.clearThreeCanvas();
    this.draw({ force: true });
  }
  panHelper(mouseXY, initialXScale, { dx, dy }, chartsToPan) {
    const {
      xAccessor,
      displayXAccessor,
      chartConfig: initialChartConfig,
    } = this.state;
    const { filterData } = this.state;
    const { fullData } = this;
    const { postCalculator } = this.props;

    if (isNotDefined(initialXScale.invert))
      throw new Error(
        "xScale provided does not have an invert() method." +
          "You are likely using an ordinal scale. This scale does not support zoom, pan",
      );

    const newDomain = initialXScale
      .range()
      .map((x) => x - dx)
      .map(initialXScale.invert);

    const { plotData: beforePlotData, domain } = filterData(
      fullData,
      newDomain,
      xAccessor,
      initialXScale,
      {
        currentPlotData: this.hackyWayToStopPanBeyondBounds__plotData,
        currentDomain: this.hackyWayToStopPanBeyondBounds__domain,
      },
    );

    const updatedScale = initialXScale.copy().domain(domain);
    const plotData = postCalculator(beforePlotData);

    const currentItem = getCurrentItem(
      updatedScale,
      xAccessor,
      mouseXY,
      plotData,
    );
    const chartConfig = getChartConfigWithUpdatedYScales(
      initialChartConfig,
      { plotData, xAccessor, displayXAccessor, fullData },
      updatedScale.domain(),
      dy,
      chartsToPan,
    );
    const currentCharts = getCurrentCharts(chartConfig, mouseXY);

    return {
      xScale: updatedScale,
      plotData,
      chartConfig,
      mouseXY,
      currentCharts,
      currentItem,
    };
  }
  handlePan(mousePosition, panStartXScale, dxdy, chartsToPan, e) {
    if (!this.waitingForPanAnimationFrame) {
      this.waitingForPanAnimationFrame = true;

      this.hackyWayToStopPanBeyondBounds__plotData =
        this.hackyWayToStopPanBeyondBounds__plotData || this.state.plotData;
      this.hackyWayToStopPanBeyondBounds__domain =
        this.hackyWayToStopPanBeyondBounds__domain ||
        this.state.xScale.domain();

      const state = this.panHelper(
        mousePosition,
        panStartXScale,
        dxdy,
        chartsToPan,
      );

      this.hackyWayToStopPanBeyondBounds__plotData = state.plotData;
      this.hackyWayToStopPanBeyondBounds__domain = state.xScale.domain();

      this.panInProgress = true;

      this.triggerEvent("pan", state, e);

      this.mutableState = {
        mouseXY: state.mouseXY,
        currentItem: state.currentItem,
        currentCharts: state.currentCharts,
      };
      requestAnimationFrame(() => {
        this.waitingForPanAnimationFrame = false;
        this.clearBothCanvas();
        this.draw({ trigger: "pan" });
      });
    }
  }
  handlePanEnd(mousePosition, panStartXScale, dxdy, chartsToPan, e) {
    const state = this.panHelper(
      mousePosition,
      panStartXScale,
      dxdy,
      chartsToPan,
    );
    this.hackyWayToStopPanBeyondBounds__plotData = null;
    this.hackyWayToStopPanBeyondBounds__domain = null;

    const { xScale, plotData, chartConfig } = state;

    this.triggerEvent("panend", state, e);

    // Update state synchronously BEFORE allowing renders
    // This prevents flicker by ensuring state is ready when panInProgress becomes false
    const { xAccessor } = this.state;
    const { fullData } = this;
    const firstItem = head(fullData);
    const start = head(xScale.domain());
    const end = xAccessor(firstItem);
    const { onLoadMore, onXExtentsChange } = this.props;

    // Set state first, then release panInProgress
    this.setState(
      {
        xScale,
        plotData,
        chartConfig,
      },
      () => {
        // Only release pan lock after state is committed
        this.panInProgress = false;

        // Clear and draw in same frame to avoid flicker
        this.clearThreeCanvas();
        this.draw({ trigger: "panend" });

        if (start < end) onLoadMore(start, end);

        // Notify about xExtents change after pan completes
        if (onXExtentsChange) {
          const xExtents = xScale.domain();
          setTimeout(() => {
            try {
              onXExtentsChange(xExtents);
            } catch (error) {
              console.error("Error in onXExtentsChange callback:", error);
            }
          }, 0);
        }
      },
    );
  }
  handleMouseDown(mousePosition, currentCharts, e) {
    this.triggerEvent("mousedown", this.mutableState, e);
  }
  handleMouseEnter(e) {
    this.triggerEvent(
      "mouseenter",
      {
        show: true,
      },
      e,
    );
  }
  handleMouseMove(mouseXY, inputType, e, force) {
    // Time-based throttle: limit to max 60 FPS to prevent excessive draws
    const now = performance.now();
    const timeSinceLastDraw = now - this.lastMouseMoveDrawTime;

    if (
      force ||
      (!this.waitingForMouseMoveAnimationFrame &&
        timeSinceLastDraw >= this.mouseMoveThrottleInterval)
    ) {
      if (!force) {
        this.waitingForMouseMoveAnimationFrame = true;
      }
      this.lastMouseMoveDrawTime = now;

      const { chartConfig, plotData, xScale, xAccessor } = this.state;
      const currentCharts = getCurrentCharts(chartConfig, mouseXY);
      const currentItem = getCurrentItem(xScale, xAccessor, mouseXY, plotData);
      this.triggerEvent(
        "mousemove",
        {
          show: true,
          mouseXY,
          // prevMouseXY is used in interactive components
          prevMouseXY: this.prevMouseXY,
          currentItem,
          currentCharts,
        },
        e,
      );

      this.prevMouseXY = mouseXY;
      this.mutableState = {
        mouseXY,
        currentItem,
        currentCharts,
      };

      // Skip the draw cycle for forced calls (e.g. from mousedown refreshing hover state)
      // — the mousedown handler will trigger its own draw cycle
      if (!force) {
        requestAnimationFrame(() => {
          this.clearMouseCanvas();
          this.draw({ trigger: "mousemove" });
          this.waitingForMouseMoveAnimationFrame = false;
        });
      }
    }
  }
  handleMouseLeave(e) {
    this.triggerEvent("mouseleave", { show: false }, e);
    this.clearMouseCanvas();
    this.draw({ trigger: "mouseleave" });
  }
  handleDragStart({ startPos }, e) {
    this.triggerEvent("dragstart", { startPos }, e);
  }
  handleDrag({ startPos, mouseXY }, e) {
    const { chartConfig, plotData, xScale, xAccessor } = this.state;
    const currentCharts = getCurrentCharts(chartConfig, mouseXY);
    const currentItem = getCurrentItem(xScale, xAccessor, mouseXY, plotData);

    this.triggerEvent(
      "drag",
      {
        startPos,
        mouseXY,
        currentItem,
        currentCharts,
      },
      e,
    );

    this.mutableState = {
      mouseXY,
      currentItem,
      currentCharts,
    };

    requestAnimationFrame(() => {
      this.clearMouseCanvas();
      this.draw({ trigger: "drag" });
    });
  }
  handleDragEnd({ mouseXY }, e) {
    this.triggerEvent("dragend", { mouseXY }, e);

    requestAnimationFrame(() => {
      this.clearMouseCanvas();
      this.draw({ trigger: "dragend" });
    });
  }
  handleClick(mousePosition, e) {
    this.triggerEvent("click", this.mutableState, e);

    requestAnimationFrame(() => {
      this.clearMouseCanvas();
      this.draw({ trigger: "click" });
    });
  }
  handleDoubleClick(mousePosition, e) {
    this.triggerEvent("dblclick", {}, e);
  }
  getChildContext() {
    const dimensions = getDimensions(this.props);

    return {
      fullData: this.fullData,
      plotData: this.state.plotData,
      width: dimensions.width,
      height: dimensions.height,
      chartConfig: this.state.chartConfig,
      xScale: this.state.xScale,
      xAccessor: this.state.xAccessor,
      displayXAccessor: this.state.displayXAccessor,
      chartCanvasType: this.props.type,
      margin: this.props.margin,
      ratio: this.props.ratio,
      xAxisZoom: this.xAxisZoom,
      yAxisZoom: this.yAxisZoom,
      yAxisZoomStart: this.yAxisZoomStart,
      yAxisZoomUpdate: this.yAxisZoomUpdate,
      yAxisZoomCommit: this.yAxisZoomCommit,
      getCanvasContexts: this.getCanvasContexts,
      redraw: this.redraw,
      subscribe: this.subscribe,
      unsubscribe: this.unsubscribe,
      generateSubscriptionId: this.generateSubscriptionId,
      getMutableState: this.getMutableState,
      amIOnTop: this.amIOnTop,
      setCursorClass: this.setCursorClass,
      clearIfNeeded: this.clearIfNeeded,
      isDeferringDrawing: () => this.deferDrawing,
      // Provide access to the latest chartConfig (may be newer than state during zoom)
      getLatestChartConfig: () =>
        latestChartConfigOf(this.latestChartConfig, this.state.chartConfig),
    };
  }
  UNSAFE_componentWillMount() {
    const { fullData, ...state } = resetChart(this.props, true);
    this.setState(state);
    this.fullData = fullData;
  }
  // #357: re-apply deferido para o branch de skip (gesto/janela quieta pós-zoom).
  // O skip guarda this.fullData mas não renderiza; se a cotação pulada for a última
  // (símbolo parado), o render fica stale. Após a janela, re-roda o merge a partir
  // das props atuais (que já carregam o dado novo). Se ainda houver gesto, o CWRP
  // pula de novo e re-agenda — auto-corrige. clearTimeout evita empilhar timers sob
  // skips rápidos.
  scheduleQuietWindowReapply() {
    clearTimeout(this._quietWindowReapplyTimer);
    this._quietWindowReapplyTimer = setTimeout(() => {
      this.UNSAFE_componentWillReceiveProps(this.props);
    }, POST_ZOOM_QUIET_MS);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const reset = shouldResetChart(this.props, nextProps);

    const interaction = isInteractionEnabled(
      this.state.xScale,
      this.state.xAccessor,
      this.state.plotData,
    );
    const { chartConfig: initialChartConfig } = this.state;

    // Check if this xExtents prop update is from our own zoom operation
    // This prevents "bounce" during rapid scrolling where:
    // 1. We zoom to [20,100], call onXExtentsChange([20,100])
    // 2. Next zoom to [40,100] starts
    // 3. Prop update [20,100] arrives, would reset chart back to [20,100] = BOUNCE!
    const xExtentsChanged = !shallowEqual(
      this.props.xExtents,
      nextProps.xExtents,
    );

    const incomingExtents = nextProps.xExtents;

    // Check if incoming matches what we last zoomed to (legacy check)
    const isExpectedZoomUpdate =
      this.lastZoomedExtents &&
      incomingExtents &&
      Array.isArray(incomingExtents) &&
      this.lastZoomedExtents[0] === incomingExtents[0] &&
      this.lastZoomedExtents[1] === incomingExtents[1];

    // NEW: Check if this is a stale intermediate update
    // If we zoomed recently AND the incoming prop doesn't match our current internal state,
    // then it's a stale prop from an earlier zoom - skip it
    const timeSinceZoom = Date.now() - this.lastZoomTime;
    const currentDomain = this.state.xScale.domain();
    const incomingMatchesCurrent =
      incomingExtents &&
      Array.isArray(incomingExtents) &&
      typeof incomingExtents[0] === "number" &&
      typeof incomingExtents[1] === "number" &&
      Math.abs(currentDomain[0] - incomingExtents[0]) < 0.01 &&
      Math.abs(currentDomain[1] - incomingExtents[1]) < 0.01;
    const isStaleZoomProp =
      xExtentsChanged &&
      !incomingMatchesCurrent &&
      timeSinceZoom < 500 && // Within 500ms of a zoom
      incomingExtents &&
      Array.isArray(incomingExtents) &&
      typeof incomingExtents[0] === "number";

    if (isExpectedZoomUpdate) {
      // This prop update is from OUR zoom - clear the tracking
      this.lastZoomedExtents = null;
    }

    let newState;
    if (
      !interaction ||
      reset ||
      (xExtentsChanged &&
        !isExpectedZoomUpdate &&
        !isStaleZoomProp &&
        !incomingMatchesCurrent)
    ) {
      if (process.env.NODE_ENV !== "production") {
        if (!interaction)
          log("RESET CHART, changes to a non interactive chart");
        else if (reset)
          log(
            "RESET CHART, one or more of these props changed",
            CANDIDATES_FOR_RESET,
          );
        else log("xExtents changed");
      }

      // do reset
      newState = resetChart(nextProps);
      this.mutableState = {};

      // Preserve yPanEnabled and yScale domain from previous config if xExtents changed but not a full reset
      // This allows vertical pan to persist when new data arrives and shifts the view
      if (!reset && initialChartConfig) {
        newState.chartConfig = newState.chartConfig.map((config) => {
          const prevConfig = initialChartConfig.find((c) => c.id === config.id);
          if (prevConfig && prevConfig.yPanEnabled) {
            return {
              ...config,
              yPanEnabled: true,
              yScale: config.yScale.copy().domain(prevConfig.yScale.domain()),
            };
          }
          return config;
        });
      }
    } else {
      const [start, end] = this.state.xScale.domain();
      const prevLastItem = last(this.fullData);

      const calculatedState = calculateFullData(nextProps);
      const { xAccessor } = calculatedState;
      const lastItemX = xAccessor(prevLastItem);
      const lastItemWasVisible = isLastBarVisibleInExtents(
        [start, end],
        lastItemX,
      );

      if (process.env.NODE_ENV !== "production") {
        if (this.props.data !== nextProps.data)
          log(
            "data is changed but seriesName did not, change the seriesName if you wish to reset the chart and lastItemWasVisible = ",
            lastItemWasVisible,
          );
        else
          log(
            "Trivial change, may be width/height or type changed, but that does not matter",
          );
      }

      newState = updateChart(
        calculatedState,
        this.state.xScale,
        nextProps,
        lastItemWasVisible,
        initialChartConfig,
      );
    }

    const { fullData, ...state } = newState;

    // Skip state updates while a pan/zoom gesture is in flight, or within the
    // post-zoom quiet window: the gesture's setState may not have committed yet,
    // so pushing newState would overwrite it (race → bounce/"dancing"). Still
    // keep fullData current so no data is lost, then bail early.
    if (
      shouldSkipStateUpdateDuringGesture(
        this.panInProgress,
        this.zoomInProgress,
        timeSinceZoom,
      )
    ) {
      this.fullData = fullData;
      // #357: garante que o update pulado seja re-aplicado após a janela quieta,
      // mesmo que nenhuma cotação seguinte chegue (símbolo parado / último tick).
      this.scheduleQuietWindowReapply();
      return;
    }

    // Detect dimension changes (resize)
    const dimensionsChanged =
      this.props.width !== nextProps.width ||
      this.props.height !== nextProps.height;

    if (dimensionsChanged) {
      const measureToResizeMs = nextProps.layoutSnapshot?.sizingTelemetry
        ?.measuredAt
        ? Math.round(performance.now()) -
          nextProps.layoutSnapshot.sizingTelemetry.measuredAt
        : null;
      if (DEBUG_CHART_SIZING) {
        console.debug("[chart-sizing] ChartCanvas resize path", {
          chartId: nextProps["data-chart-id"],
          prevWidth: this.props.width,
          prevHeight: this.props.height,
          nextWidth: nextProps.width,
          nextHeight: nextProps.height,
          cause: nextProps.layoutSnapshot?.cause,
          sectionCount: nextProps.layoutSnapshot?.sections?.length,
          fitWidthSource:
            nextProps.layoutSnapshot?.sizingTelemetry?.source || null,
          fitWidthSequence:
            nextProps.layoutSnapshot?.sizingTelemetry?.sequence || null,
          measureToResizeMs,
        });
      }
      recordChartSizingEvent("chartCanvas.resizePath", {
        chartId: nextProps["data-chart-id"],
        prevWidth: this.props.width,
        prevHeight: this.props.height,
        nextWidth: nextProps.width,
        nextHeight: nextProps.height,
        cause: nextProps.layoutSnapshot?.cause,
        fitWidthSource:
          nextProps.layoutSnapshot?.sizingTelemetry?.source || null,
        fitWidthSequence:
          nextProps.layoutSnapshot?.sizingTelemetry?.sequence || null,
        measureToResizeMs,
      });
      // Resize: apply state synchronously (batched by React in CWRP)
      // so props (new dimensions) and state (new scales/chartConfig) commit together.
      this.fullData = fullData;

      const prevXExtents = this.state.xScale
        ? this.state.xScale.domain()
        : null;
      this.setState(state, () => {
        this.triggerEvent("pan", {
          xScale: state.xScale,
          plotData: state.plotData,
        });
        this.clearThreeCanvas();
        this.draw({
          trigger: "resize",
          chartConfig: state.chartConfig,
          xScale: state.xScale,
          plotData: state.plotData,
        });
        const { onXExtentsChange } = this.props;
        const nextXExtents = state.xScale ? state.xScale.domain() : null;
        if (
          onXExtentsChange &&
          prevXExtents &&
          nextXExtents &&
          !shallowEqual(prevXExtents, nextXExtents)
        ) {
          try {
            onXExtentsChange(nextXExtents);
          } catch (error) {
            console.error("Error in onXExtentsChange callback:", error);
          }
        }
      });
    } else {
      // Normal update: defer all drawing to setState callback
      // This ensures clear+draw happen atomically (no flicker/ghosting)
      this.reactUpdateInProgress = true;
      this.deferDrawing = true;

      // Update RAF trackers BEFORE setState to prevent RAF from thinking data changed
      if (state.plotData && state.plotData.length > 0) {
        this.lastDrawnDataLength = state.plotData.length;
        this.lastDrawnBar = state.plotData[state.plotData.length - 1];
      }

      const prevXExtents = this.state.xScale
        ? this.state.xScale.domain()
        : null;
      this.setState(state, () => {
        // Refresh interactive components + redraw with the fresh chartConfig
        // (issue #351 — order lines kept the previous symbol's yScale).
        this.redrawAfterDataUpdate(state);
        const { onXExtentsChange } = this.props;
        const nextXExtents = state.xScale ? state.xScale.domain() : null;
        if (
          onXExtentsChange &&
          prevXExtents &&
          nextXExtents &&
          !shallowEqual(prevXExtents, nextXExtents)
        ) {
          try {
            onXExtentsChange(nextXExtents);
          } catch (error) {
            console.error("Error in onXExtentsChange callback:", error);
          }
        }
        this.deferDrawing = false;
        this.reactUpdateInProgress = false;
      });
      this.fullData = fullData;
    }
  }
  componentDidUpdate(prevProps, prevState) {
    // FIX #140: Detect chartConfig height changes and trigger redraw
    // When panel heights change (e.g., resizing indicator panels), interactive
    // components (orders, fibonacci, segments) need to be notified to redraw
    // at their new Y positions.
    //
    // Performance: This runs on every ChartCanvas update, but:
    // - Comparison is O(n) where n = number of charts (typically 2-5)
    // - draw() only fires when heights actually change (during panel resize)
    // - requestAnimationFrame batches calls, heightChangeDrawPending prevents duplicates
    const prevChartConfig = prevState.chartConfig;
    const currentChartConfig = this.state.chartConfig;

    // Early exit: skip if configs are same reference or missing
    if (
      !prevChartConfig ||
      !currentChartConfig ||
      prevChartConfig === currentChartConfig
    ) {
      return;
    }

    // Early exit: skip if array lengths differ (handled by other logic)
    if (prevChartConfig.length !== currentChartConfig.length) {
      return;
    }

    // Check if any chart's height or origin changed
    let layoutChanged = false;
    for (let i = 0; i < currentChartConfig.length; i++) {
      const config = currentChartConfig[i];
      const prevConfig = prevChartConfig[i];
      if (
        config.height !== prevConfig.height ||
        config.origin?.[1] !== prevConfig.origin?.[1]
      ) {
        layoutChanged = true;
        break; // Exit early on first change
      }
    }

    if (layoutChanged && !this.heightChangeDrawPending) {
      this.heightChangeDrawPending = true;
      // Trigger redraw for all subscribed components
      // Pass current chartConfig so components with shouldComponentUpdate can use fresh yScale
      requestAnimationFrame(() => {
        this.heightChangeDrawPending = false;
        this.draw({
          trigger: "pan",
          force: true,
          chartConfig: this.state.chartConfig, // Pass fresh chartConfig for stale components
        });
      });
    }
  }
  resetYDomain(chartId) {
    const { chartConfig } = this.state;
    let changed = false;
    const newChartConfig = chartConfig.map((each) => {
      if (
        (isNotDefined(chartId) || each.id === chartId) &&
        !shallowEqual(each.yScale.domain(), each.realYDomain)
      ) {
        changed = true;
        return {
          ...each,
          yScale: each.yScale.domain(each.realYDomain),
          yPanEnabled: false,
        };
      }
      return each;
    });

    if (changed) {
      // Use setState callback to ensure clear+draw happen atomically (no flash)
      this.setState(
        {
          chartConfig: newChartConfig,
        },
        () => {
          // Trigger event so listener() updates moreProps on all subscribed components
          this.triggerEvent("pan", { chartConfig: newChartConfig });
          this.clearThreeCanvas();
          this.draw({
            trigger: "resetYDomain",
            force: true,
            chartConfig: newChartConfig,
          });
        },
      );
    }
  }
  shouldComponentUpdate() {
    // Only block renders during pan (zoom needs render for setState callback)
    return !this.panInProgress;
  }
  render() {
    const {
      type,
      height,
      width,
      margin,
      className,
      zIndex,
      defaultFocus,
      ratio,
      mouseMoveEvent,
      panEvent,
      zoomEvent,
    } = this.props;
    const { useCrossHairStyleCursor, onSelect } = this.props;

    const { plotData, xScale, xAccessor, chartConfig } = this.state;
    const dimensions = getDimensions(this.props);

    const interaction = isInteractionEnabled(xScale, xAccessor, plotData);

    const cursorStyle = useCrossHairStyleCursor && interaction;
    const cursor = getCursorStyle();

    // Memoize style objects to prevent recreation on every render
    const containerStyle = { position: "relative", width, height };
    const svgStyle = {
      position: "absolute",
      zIndex: zIndex + 5,
      left: 0,
      top: 0,
    };

    return (
      <div
        style={containerStyle}
        className={className}
        onClick={onSelect}
        data-evo-id="chartcanvas-container"
      >
        <CanvasContainer
          ref={this.saveCanvasContainerNode}
          type={type}
          ratio={ratio}
          width={width}
          height={height}
          zIndex={zIndex}
        />
        <svg
          className={className}
          width={width}
          height={height}
          style={svgStyle}
        >
          {cursor}
          <defs>
            <clipPath id="chart-area-clip">
              <rect
                x="0"
                y="0"
                width={dimensions.width}
                height={dimensions.height}
              />
            </clipPath>
            {chartConfig.map((each, idx) => (
              <clipPath key={idx} id={`chart-area-clip-${each.id}`}>
                <rect x="0" y="0" width={each.width} height={each.height} />
              </clipPath>
            ))}
          </defs>
          <g transform={`translate(${margin.left + 0.5}, ${margin.top + 0.5})`}>
            <EventCapture
              ref={this.saveEventCaptureNode}
              useCrossHairStyleCursor={cursorStyle}
              mouseMove={mouseMoveEvent && interaction}
              zoom={zoomEvent && interaction}
              pan={panEvent && interaction}
              width={dimensions.width}
              height={dimensions.height}
              chartConfig={chartConfig}
              xScale={xScale}
              xAccessor={xAccessor}
              focus={defaultFocus}
              disableInteraction={this.props.disableInteraction}
              getAllPanConditions={this.getAllPanConditions}
              onContextMenu={this.handleContextMenu}
              onClick={this.handleClick}
              onDoubleClick={this.handleDoubleClick}
              onMouseDown={this.handleMouseDown}
              onMouseMove={this.handleMouseMove}
              onMouseEnter={this.handleMouseEnter}
              onMouseLeave={this.handleMouseLeave}
              onDragStart={this.handleDragStart}
              onDrag={this.handleDrag}
              onDragComplete={this.handleDragEnd}
              onZoom={this.handleZoom}
              onPinchZoom={this.handlePinchZoom}
              onPinchZoomEnd={this.handlePinchZoomEnd}
              onPan={this.handlePan}
              onPanEnd={this.handlePanEnd}
              data-evo-id="chartcanvas-event-capture"
            />

            <g className="react-stockcharts-avoid-interaction">
              {this.props.children}
            </g>
          </g>
        </svg>
      </div>
    );
  }
}

function isInteractionEnabled(xScale, xAccessor, data) {
  const interaction =
    !isNaN(xScale(xAccessor(head(data)))) && isDefined(xScale.invert);
  return interaction;
}

ChartCanvas.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  margin: PropTypes.object,
  ratio: PropTypes.number.isRequired,
  // interval: PropTypes.oneOf(["D", "W", "M"]), // ,"m1", "m5", "m15", "W", "M"
  type: PropTypes.oneOf(["svg", "hybrid"]),
  pointsPerPxThreshold: PropTypes.number,
  minPointsPerPxThreshold: PropTypes.number,
  data: PropTypes.array.isRequired,
  // initialDisplay: PropTypes.number,
  xAccessor: PropTypes.func,
  xExtents: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  zoomAnchor: PropTypes.func,

  className: PropTypes.string,
  seriesName: PropTypes.string.isRequired,
  zIndex: PropTypes.number,
  children: PropTypes.node.isRequired,
  xScale: PropTypes.func.isRequired,
  postCalculator: PropTypes.func,
  flipXScale: PropTypes.bool,
  useCrossHairStyleCursor: PropTypes.bool,
  padding: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      left: PropTypes.number,
      right: PropTypes.number,
    }),
  ]),
  defaultFocus: PropTypes.bool,
  zoomMultiplier: PropTypes.number,
  onLoadMore: PropTypes.func,
  displayXAccessor: function (props, propName /* , componentName */) {
    if (isNotDefined(props[propName])) {
      console.warn(
        "`displayXAccessor` is not defined," +
          " will use the value from `xAccessor` as `displayXAccessor`." +
          " This might be ok if you do not use a discontinuous scale" +
          " but if you do, provide a `displayXAccessor` prop to `ChartCanvas`",
      );
    } else if (typeof props[propName] !== "function") {
      return new Error("displayXAccessor has to be a function");
    }
  },
  mouseMoveEvent: PropTypes.bool,
  panEvent: PropTypes.bool,
  clamp: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.func,
  ]),
  zoomEvent: PropTypes.bool,
  onSelect: PropTypes.func,
  onVisibleBarsChange: PropTypes.func,
  onXExtentsChange: PropTypes.func,
  disableInteraction: PropTypes.bool,
};

ChartCanvas.defaultProps = {
  margin: { top: 20, right: 30, bottom: 30, left: 80 },
  type: "hybrid",
  pointsPerPxThreshold: 2,
  minPointsPerPxThreshold: 1 / 100,
  className: "react-stockchart",
  zIndex: 1,
  xExtents: [min, max],
  postCalculator: identity,
  padding: 0,
  xAccessor: identity,
  flipXScale: false,
  useCrossHairStyleCursor: true,
  defaultFocus: true,
  onLoadMore: noop,
  onSelect: noop,
  mouseMoveEvent: true,
  panEvent: true,
  zoomEvent: true,
  zoomMultiplier: 1.1,
  clamp: false,
  zoomAnchor: rightDomainBasedZoomAnchor,
  // ratio: 2,
  disableInteraction: false,
};

ChartCanvas.childContextTypes = {
  plotData: PropTypes.array,
  fullData: PropTypes.array,
  chartConfig: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      origin: PropTypes.arrayOf(PropTypes.number).isRequired,
      padding: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
          top: PropTypes.number,
          bottom: PropTypes.number,
        }),
      ]),
      yExtents: PropTypes.arrayOf(PropTypes.func),
      yExtentsProvider: PropTypes.func,
      yScale: PropTypes.func.isRequired,
      mouseCoordinates: PropTypes.shape({
        at: PropTypes.string,
        format: PropTypes.func,
      }),
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }),
  ).isRequired,
  xScale: PropTypes.func.isRequired,
  xAccessor: PropTypes.func.isRequired,
  displayXAccessor: PropTypes.func.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  chartCanvasType: PropTypes.oneOf(["svg", "hybrid"]).isRequired,
  margin: PropTypes.object.isRequired,
  ratio: PropTypes.number.isRequired,
  getCanvasContexts: PropTypes.func,
  xAxisZoom: PropTypes.func,
  yAxisZoom: PropTypes.func,
  yAxisZoomStart: PropTypes.func,
  yAxisZoomUpdate: PropTypes.func,
  yAxisZoomCommit: PropTypes.func,
  amIOnTop: PropTypes.func,
  redraw: PropTypes.func,
  subscribe: PropTypes.func,
  unsubscribe: PropTypes.func,
  setCursorClass: PropTypes.func,
  generateSubscriptionId: PropTypes.func,
  getMutableState: PropTypes.func,
  clearIfNeeded: PropTypes.func,
  isDeferringDrawing: PropTypes.func,
  getLatestChartConfig: PropTypes.func,
};

ChartCanvas.ohlcv = (d) => ({
  date: d.date,
  open: d.open,
  high: d.high,
  low: d.low,
  close: d.close,
  volume: d.volume,
});

export default ChartCanvas;
